import type { Tables } from '@/types/db';

/** Eén telefoongesprek (Voys-webhook vult de tabel). Afgeleid van het DB-schema. */
export type PhoneCall = Tables<'phone_calls'>;

/** Gesprek met de geneste koppelingen zoals de service ze selecteert. */
export interface PhoneCallWithRelations extends PhoneCall {
  contacts: { id: string; name: string } | null;
  leads: { id: string; contact_name: string; work_type: string | null } | null;
  projects: {
    id: string;
    name: string;
    project_number: string | null;
    address: string | null;
    contact_person_name: string | null;
  } | null;
}

/** Status van een klik-en-bel-gesprek zoals de Voys Klik&Bel-API die geeft. */
export type DialStatus =
  | null
  | 'dialing_a'
  | 'dialing_b'
  | 'connected'
  | 'ended'
  | 'disconnected'
  | 'failing_a'
  | 'failing_b';

/** AI-classificatielabels zoals de voys-webhook edge function ze zet. */
export const AI_LABELS = [
  'klant',
  'potentiele_klant',
  'leverancier',
  'verkoop',
  'prive_intern',
  'overig',
] as const;

export type AiLabel = (typeof AI_LABELS)[number];

/** Variants van `src/components/ui/Badge.tsx` (niet geëxporteerd door Badge zelf). */
type BadgeVariant = 'default' | 'muted' | 'success' | 'warning' | 'danger';

/** NL-weergavenaam + Badge-variant per AI-label. */
export const AI_LABEL_INFO: Record<AiLabel, { label: string; variant: BadgeVariant }> = {
  klant: { label: 'Klant', variant: 'success' },
  potentiele_klant: { label: 'Potentiële klant', variant: 'default' },
  leverancier: { label: 'Leverancier', variant: 'warning' },
  verkoop: { label: 'Verkoop', variant: 'muted' },
  prive_intern: { label: 'Privé/intern', variant: 'muted' },
  overig: { label: 'Overig', variant: 'muted' },
};

/** ai_meta (jsonb) veilig uitgelezen. */
export interface PhoneCallMeta {
  reden_vlag: string | null;
  terugbelafspraak: string | null;
}

export function getCallMeta(call: PhoneCall): PhoneCallMeta {
  const meta =
    call.ai_meta && typeof call.ai_meta === 'object' && !Array.isArray(call.ai_meta)
      ? (call.ai_meta as Record<string, unknown>)
      : {};
  const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v : null);
  return {
    reden_vlag: str(meta.reden_vlag),
    terugbelafspraak: str(meta.terugbelafspraak),
  };
}

/** Info over het gekoppelde label bij een (mogelijk onbekend) ai_label. */
export function getLabelInfo(
  aiLabel: string | null,
): { label: string; variant: BadgeVariant } | null {
  if (!aiLabel) return null;
  return AI_LABEL_INFO[aiLabel as AiLabel] ?? { label: aiLabel, variant: 'muted' };
}

/** Inkomend gesprek dat wel is beëindigd maar niet normaal is afgerond. */
export function isMissedCall(call: PhoneCall): boolean {
  return call.direction === 'inbound' && call.status === 'ended' && call.reason !== 'completed';
}

/** Het externe nummer (beller bij inkomend, gebelde bij uitgaand). */
export function getExternalNumber(call: PhoneCall): string {
  const number = call.direction === 'inbound' ? call.caller_number : call.destination_number;
  return number || 'Anoniem';
}

/** Gespreksduur als mm:ss, of null als het gesprek (nog) geen duur heeft. */
export function getCallDuration(call: PhoneCall): string | null {
  if (!call.started_at || !call.ended_at) return null;
  const seconds = Math.round(
    (new Date(call.ended_at).getTime() - new Date(call.started_at).getTime()) / 1000,
  );
  if (!Number.isFinite(seconds) || seconds < 0) return null;
  const mm = Math.floor(seconds / 60);
  const ss = seconds % 60;
  return `${mm}:${String(ss).padStart(2, '0')}`;
}

/** Datum/tijd van het gesprek in nl-NL (started_at, met created_at als vangnet). */
export function formatCallMoment(call: PhoneCall): string {
  const iso = call.started_at ?? call.created_at;
  return new Date(iso).toLocaleString('nl-NL', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
